import { LabelButton } from '@teamsnap/snap-ui';
import { AMPLITUDE_PRE_ACTIONS, useAmplitude } from 'hooks/amplitude';
import { FormPage, LoadingType } from 'hooks/postRegEditDrawer';
import {
  usePostRegEditActions,
  usePostRegEditDrawer,
} from 'hooks/postRegEditDrawer/usePostRegEditDrawer';
import * as React from 'react';
import { ConsentCheckbox } from './Review/ConsentCheckbox';

interface PostRegEditDrawerFooterActionsProps {
  closeDrawer: () => void;
  openConfirmation: () => void;
}

const PostRegEditDrawerFooterActions = ({
  closeDrawer,
  openConfirmation,
}: PostRegEditDrawerFooterActionsProps) => {
  const {
    formPage,
    dirtyFields,
    isFormValid,
    loading,
    previewLineItems,
    pendingPayment,
  } = usePostRegEditDrawer();
  const { setFormPage, setPendingEdits, validateAndSubmit } =
    usePostRegEditActions();
  const [accepted, setAccepted] = React.useState(false);
  const { amplitudeTrack } = useAmplitude();
  const hasEdits = Object.keys(dirtyFields).length > 0;
  const hasBalanceChange =
    previewLineItems?.currentBalance !== previewLineItems?.newBalance;
  const hasError = pendingPayment?.error || Object.keys(pendingPayment?.fieldErrors || {}).length > 0;

  if (formPage === FormPage.Reviewing) {
    return (
      <div className="sui-flex sui-flex-col sui-gap-1 sui-w-full">
        <ConsentCheckbox
          previewLineItems={previewLineItems}
          pendingPayment={pendingPayment}
          accepted={accepted}
          setAccepted={(accepted) => {
            setAccepted(accepted);
            accepted &&
              amplitudeTrack.preActions(
                AMPLITUDE_PRE_ACTIONS.CONSENT_DIALOGUE_CLICKED
              );
          }}
        />
        <div className="sui-flex sui-gap-2 sui-justify-end">
          <LabelButton
            key={'return'}
            labelText="Back to editing"
            variantType="secondary"
            type="button"
            data-testid="PostRegEditForm--edit-button"
            onClick={() => {
              amplitudeTrack.preActions(
                AMPLITUDE_PRE_ACTIONS.BACK_TO_EDITING_CLICKED
              );
              setFormPage(FormPage.Editing);
              setAccepted(false);
            }}
          />
          <LabelButton
            key={'update'}
            labelText="Update registration and order"
            variantType="primary"
            type="submit"
            loading={loading[LoadingType.SubmitEdits]}
            data-testid="PostRegEditForm--update-button"
            onClick={() => {
              amplitudeTrack.preActions(
                AMPLITUDE_PRE_ACTIONS.SAVE_CHANGES_CLICKED
              );
              validateAndSubmit().then(() => {
                openConfirmation();
              }).catch(() => {
                // do nothing, error will be displayed in toast or inline
              });
            }}
            disabled={(hasBalanceChange && !accepted) || hasError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sui-flex  sui-gap-2 sui-justify-end">
      <LabelButton
        key={'cancel'}
        labelText="Cancel"
        variantType="secondary"
        type="button"
        data-testid="PostRegEditForm--cancel-button"
        onClick={() => {
          amplitudeTrack.preActions(AMPLITUDE_PRE_ACTIONS.CANCEL_CLICKED);
          closeDrawer();
        }}
      />
      <LabelButton
        key={'review'}
        disabled={!hasEdits || !isFormValid || loading[LoadingType.UploadFile]}
        labelText="Review updates"
        variantType="primary"
        data-testid="PostRegEditForm--review-button"
        onClick={() => {
          amplitudeTrack.preActions(
            AMPLITUDE_PRE_ACTIONS.REVIEW_UPDATES_CLICKED
          );
          setPendingEdits();
          setFormPage(FormPage.Reviewing);
        }}
      />
    </div>
  );
};

export default PostRegEditDrawerFooterActions;
